"use client";

import React from "react";
import {
  useFetchNewsArticles,
  useFetchNewsArticlesCount,
} from "@/apis/queryFunctions/news";
import appendImageUrlFromAPI from "@/utils/appendImageUrlFromAPI";
import AppBreadcrumbs from "@/components/Common/AppBreadcrumbs";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { motion, useMotionValue, animate } from "framer-motion";
import { ChevronLeft, ChevronRight, ArrowRight, Calendar } from "lucide-react";
import { useHeader } from "@/context/HeaderLayoutContext";
import { useNewsBanner } from "@/hooks/useNewsBanner";
import { useTranslatedText } from "@/hooks/useProductTranslation";
import styles from "./tinTuc.module.css";

function NewsCardTitle({ title, locale }: { title: string; locale: string }) {
  const displayTitle = useTranslatedText(title, locale);
  return <>{displayTitle}</>;
}

export default function TinTucPage() {
  const pathname = usePathname();
  const locale = pathname.split("/")[1] || "vi";

  const breadcrumbs = [
    { title: locale === "vi" ? "Trang chủ" : "Home", href: `/${locale}` },
    { title: locale === "vi" ? "Tin tức" : "News", href: "#" },
  ];

  const bannerImg = useNewsBanner("banner-news", "/images/new_banner_main.png", locale);

  const { setHeaderStyle } = useHeader();

  React.useEffect(() => {
    setHeaderStyle({ transparent: true });
    return () => setHeaderStyle({ transparent: false });
  }, [setHeaderStyle]);

  // Fetch Featured News
  const { data: featuredNews } = useFetchNewsArticles({
    page: 1,
    limit: 15,
    condition: {
      ngon_ngu: locale,
      shared: true,
      loai_tin_tuc: {
        $in: [
          "Tin tức cà phê",
          "Tin tức coffee",
          "Tin tức trà",
          "Tin tức giá nông sản",
          "Tin tức nông sản",
          "Tin tức Việt Thiên",
          "Tin tức",
        ],
      },
    },
  });

  // Fetch 3 Main Categories
  const { data: coffeeNews, isLoading: isCoffeeLoading } = useFetchNewsArticles(
    {
      page: 1,
      limit: 4,
      condition: {
        ngon_ngu: locale,
        loai_tin_tuc: { $in: ["Tin tức cà phê", "Tin tức coffee", "Cà phê"] },
      },
    }
  );

  const { data: teaNews, isLoading: isTeaLoading } = useFetchNewsArticles({
    page: 1,
    limit: 4,
    condition: {
      ngon_ngu: locale,
      loai_tin_tuc: { $in: ["Tin tức trà", "Trà", "Tea"] },
    },
  });

  const { data: agriNews, isLoading: isAgriLoading } = useFetchNewsArticles({
    page: 1,
    limit: 4,
    condition: {
      ngon_ngu: locale,
      loai_tin_tuc: {
        $in: ["Tin tức giá nông sản", "Tin tức nông sản", "Nông sản"],
      },
    },
  });

  // Carousel Logic
  const carouselRef = React.useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(true);

  const scrollCarousel = (direction: "left" | "right") => {
    if (!carouselRef.current) return;
    const containerWidth = carouselRef.current.offsetWidth;
    const scrollAmount = containerWidth * 0.8;
    const currentX = x.get();
    let targetX =
      direction === "left" ? currentX + scrollAmount : currentX - scrollAmount;
    const maxScroll = -(carouselRef.current.scrollWidth - containerWidth);
    targetX = Math.min(0, Math.max(maxScroll, targetX));
    animate(x, targetX, { type: "spring", stiffness: 300, damping: 30 });
  };

  React.useEffect(() => {
    return x.on("change", (val) => {
      if (!carouselRef.current) return;
      const maxScroll = -(
        carouselRef.current.scrollWidth - carouselRef.current.offsetWidth
      );
      setCanScrollLeft(val < -10);
      setCanScrollRight(val > maxScroll + 10);
    });
  }, [x, featuredNews]);

  function renderNewsList(news: any[], isLoading: boolean) {
    if (isLoading)
      return [...Array(4)].map((_, i) => (
        <div
          key={i}
          className={styles.skeletonCard}
          style={{ height: "150px" }}
        />
      ));
    if (!news || news.length === 0) return null;

    const [first, ...others] = news;

    return (
      <>
        {/* Large Main Card */}
        <Link
          href={`/${locale}/tin-tuc/${first.slug}`}
          className={styles.largeCard}
        >
          <Image
            src={appendImageUrlFromAPI({ src: first.picture, size: "m" })}
            alt={first.title}
            fill
            style={{ objectFit: "cover" }}
          />
          <div className={styles.largeCardOverlay}>
            <h3 className={styles.largeTitle}>
              <NewsCardTitle title={first.title} locale={locale} />
            </h3>
            <p className={styles.largeDate}>
              {new Date(first.date_updated).toLocaleDateString(locale)}
            </p>
          </div>
        </Link>

        {/* Small List Cards */}
        <div className={styles.smallList}>
          {others.map((item) => (
            <Link
              key={item._id}
              href={`/${locale}/tin-tuc/${item.slug}`}
              className={styles.smallCard}
            >
              <div className={styles.smallImgWrapper}>
                <Image
                  src={appendImageUrlFromAPI({ src: item.picture, size: "m" })}
                  alt={item.title}
                  fill
                  style={{ objectFit: "cover" }}
                />
              </div>
              <div className={styles.smallContent}>
                <h4 className={styles.smallTitle}>
                  <NewsCardTitle title={item.title} locale={locale} />
                </h4>
                <p className={styles.smallDate}>
                  <Calendar size={12} />{" "}
                  {new Date(item.date_updated).toLocaleDateString(locale)}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </>
    );
  }

  return (
    <div className={styles.wrapper}>
      {/* Hero Banner */}
      <section className={styles.hero}>
        <div className={styles.heroBg}>
          <Image
            src={bannerImg}
            alt={locale === "vi" ? "Tin tức" : "News"}
            fill
            priority
            sizes="100vw"
            className={styles.bannerBgImage}
          />
          <div className={styles.heroMainContent}>
            <h1 className={styles.heroTitle}>
              {locale === "vi" ? "Tin tức" : "News"}
            </h1>
          </div>
        </div>
      </section>

      <div className={styles.container}>
        {/* Breadcrumbs at top as user requested */}
        <div style={{ marginTop: "20px", marginBottom: "20px" }}>
          <AppBreadcrumbs items={breadcrumbs} isLoading={false} />
        </div>

        {/* Featured Section */}
        <div className={styles.featuredHeader}>
          <h2 className={styles.featuredTitle}>
            {locale === "vi" ? "Tin tức nổi bật" : "Featured News"}
          </h2>
        </div>

        <div className={styles.carouselWrapper}>
          <button
            className={`${styles.carouselBtn} ${styles.prevBtn}`}
            onClick={() => scrollCarousel("left")}
            style={{ opacity: canScrollLeft ? 1 : 0.3 }}
          >
            <ChevronLeft size={24} />
          </button>
          <div className={styles.featuredCarouselViewport}>
            {featuredNews ? (
              <motion.div
                ref={carouselRef}
                className={styles.featuredCarousel}
                style={{ x }}
                drag="x"
                dragConstraints={{
                  left: carouselRef.current
                    ? -(
                        carouselRef.current.scrollWidth -
                        carouselRef.current.offsetWidth
                      )
                    : 0,
                  right: 0,
                }}
              >
                {featuredNews?.map((item: any) => (
                  <Link
                    key={item._id}
                    href={`/${locale}/tin-tuc/${item.slug}`}
                    className={styles.featuredCard}
                  >
                    <div
                      className={styles.imgWrapper}
                      style={{ aspectRatio: "16/9" }}
                    >
                      <Image
                        src={appendImageUrlFromAPI({
                          src: item.picture,
                          size: "m",
                        })}
                        alt={item.title}
                        fill
                        style={{ objectFit: "cover" }}
                      />
                    </div>
                    <div className={styles.content}>
                      <h3
                        className={styles.title}
                        style={{ fontSize: "14px", height: "auto" }}
                      >
                        <NewsCardTitle title={item.title} locale={locale} />
                      </h3>
                      <p className={styles.date}>
                        {new Date(item.date_updated).toLocaleDateString(locale)}
                      </p>
                    </div>
                  </Link>
                ))}
              </motion.div>
            ) : (
              <div className={styles.featuredCarousel}>
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className={styles.skeletonCard}
                    style={{ minWidth: "320px", flex: "0 0 320px" }}
                  />
                ))}
              </div>
            )}
          </div>
          <button
            className={`${styles.carouselBtn} ${styles.nextBtn}`}
            onClick={() => scrollCarousel("right")}
            style={{ opacity: canScrollRight ? 1 : 0.3 }}
          >
            <ChevronRight size={24} />
          </button>
        </div>

        {/* 3 Main Categories Section */}
        <div className={styles.threeColumnLayout}>
          <div className={styles.newsColumn}>
            <div className={styles.columnHeader}>
              <h2 className={styles.columnTitle}>
                {locale === "vi" ? "Tin tức trà" : "Tea News"}
              </h2>
            </div>
            {renderNewsList(teaNews || [], isTeaLoading)}
            <Link
              href={`/${locale}/tin-tuc/tra`}
              className={styles.viewMoreBtn}
            >
              {locale === "vi" ? "Xem thêm" : "See more"}{" "}
              <ArrowRight size={18} />
            </Link>
          </div>

          <div className={styles.columnDivider} />

          <div className={styles.newsColumn}>
            <div className={styles.columnHeader}>
              <h2 className={styles.columnTitle}>
                {locale === "vi" ? "Tin tức cafe" : "Coffee News"}
              </h2>
            </div>
            {renderNewsList(coffeeNews || [], isCoffeeLoading)}
            <Link
              href={`/${locale}/tin-tuc/ca-phe`}
              className={styles.viewMoreBtn}
            >
              {locale === "vi" ? "Xem thêm" : "See more"}{" "}
              <ArrowRight size={18} />
            </Link>
          </div>

          <div className={styles.columnDivider} />

          <div className={styles.newsColumn}>
            <div className={styles.columnHeader}>
              <h2 className={styles.columnTitle}>
                {locale === "vi" ? "Tin tức nông sản" : "Agri News"}
              </h2>
            </div>
            {renderNewsList(agriNews || [], isAgriLoading)}
            <Link
              href={`/${locale}/tin-tuc/nong-san`}
              className={styles.viewMoreBtn}
            >
              {locale === "vi" ? "Xem thêm" : "See more"}{" "}
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
